import React, { useState, useEffect, useCallback } from 'react';
import { Delete, RotateCcw } from 'lucide-react';

function ScientificCalculator() {
  const [display, setDisplay] = useState('0');
  const [isRad, setIsRad] = useState(false);

  const factorial = (n) => {
    if (n < 0 || n > 170) return NaN;
    if (n === 0 || n === 1) return 1;
    let res = 1;
    for (let i = 2; i <= n; i++) res *= i;
    return res;
  };

  const evaluateExpression = useCallback((expr) => {
    expr = expr.replace(/(\d+)%/g, '($1/100)');
    expr = expr.replace(/π/g, Math.PI);
    expr = expr.replace(/e(?![x^])/g, Math.E); 
    expr = expr.replace(/(\d)(π)/g, '$1*$2');
    expr = expr.replace(/(π)(\d)/g, '$1*$2');
    expr = expr.replace(/\)(\d)/g, ')*$1');
    expr = expr.replace(/(\d)\(/g, '$1*(');
    expr = expr.replace(/×/g, '*');
    expr = expr.replace(/÷/g, '/');
    expr = expr.replace(/sin\(/g, isRad ? 'Math.sin(' : 'Math.sin(Math.PI/180*');
    expr = expr.replace(/cos\(/g, isRad ? 'Math.cos(' : 'Math.cos(Math.PI/180*');
    expr = expr.replace(/tan\(/g, isRad ? 'Math.tan(' : 'Math.tan(Math.PI/180*');
    expr = expr.replace(/ln\(/g, 'Math.log(');
    expr = expr.replace(/log\(/g, 'Math.log10(');
    expr = expr.replace(/sqrt\(/g, 'Math.sqrt(');
    expr = expr.replace(/(\d+)!/g, (match, num) => factorial(parseInt(num)));
    expr = expr.replace(/x²\(([^)]+)\)/g, '(($1)*($1))');
    expr = expr.replace(/x³\(([^)]+)\)/g, '(($1)*($1)*($1))');
    expr = expr.replace(/e\^x\(([^)]+)\)/g, 'Math.exp($1)');
    expr = expr.replace(/10\^x\(([^)]+)\)/g, 'Math.pow(10,$1)');
    expr = expr.replace(/1\/x\(([^)]+)\)/g, '(1/($1))');
    expr = expr.replace(/\^/g, '**');
    return expr;
  }, [isRad]);

  const handleEquals = useCallback(() => {
    try {
      let expr = display;

      if (!expr || expr === '0' || expr === 'Error') return;

      const openParens = (expr.match(/\(/g) || []).length;
      const closeParens = (expr.match(/\)/g) || []).length;
      if (openParens > closeParens) {
        expr += ')'.repeat(openParens - closeParens);
      }

      const evaluatedExpr = evaluateExpression(expr);

      // eslint-disable-next-line no-eval
      const result = eval(evaluatedExpr);

      if (isNaN(result) || !isFinite(result)) {
        setDisplay('Error');
      } else {
        if (Math.abs(result) < 1e-10 && result !== 0) {
          setDisplay('0');
        } else if (Math.abs(result) > 1e10) {
          setDisplay(result.toExponential(6));
        } else {
          setDisplay(String(parseFloat(result.toFixed(10))));
        }
      }
    } catch (error) {
      setDisplay('Error');
    }
  }, [display, evaluateExpression]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleEquals();
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleEquals]);

  const handleNumber = (num) => {
    if (display === '0' || display === 'Error') {
      setDisplay(num);
    } else {
      setDisplay(display + num);
    }
  };

  const handleDecimal = () => {
    if (!display.includes('.')) {
      setDisplay(display + '.');
    }
  };

  const handleClear = () => {
    setDisplay('0');
  };

  const handleBackspace = () => {
    if (display.length > 1 && display !== '0' && display !== 'Error') {
      setDisplay(display.slice(0, -1));
    } else {
      setDisplay('0');
    }
  };

  const handleFunction = (func) => {
    if (display === '0' || display === 'Error') {
      setDisplay(`${func}(`);
    } else {
      setDisplay(display + func + '(');
    }
  };

  const handleOperation = (op) => {
    if (display && display !== 'Error' && !display.endsWith(op)) {
      setDisplay(display + op);
    }
  };

  const handleParenthesis = (paren) => {
    if (display === '0') {
      if (paren === '(') {
        setDisplay('(');
      }
    } else {
      setDisplay(display + paren);
    }
  };

  const handleConstant = (constant) => {
    let value = '';
    if (constant === 'π') value = 'π';
    else if (constant === 'e') value = 'e';

    if (display === '0' || display === 'Error') {
      setDisplay(value);
    } else {
      const lastChar = display[display.length - 1];
      if (/\d/.test(lastChar)) {
        setDisplay(display + '*' + value);
      } else {
        setDisplay(display + value);
      }
    }
  };

  const handlePercentage = () => {
    if (display && display !== 'Error' && !display.endsWith('%')) {
      setDisplay(display + '%');
    }
  };

  const Button = ({ children, onClick, className = '', variant = 'default' }) => {
    const baseClass =
      'h-8 sm:h-10 md:h-12 rounded-lg sm:rounded-xl md:rounded-2xl font-semibold text-xs sm:text-sm md:text-base transition-all duration-300 active:scale-95 shadow-lg';
    const variants = {
      default:
        'bg-white hover:bg-gradient-to-br hover:from-purple-100 hover:to-pink-100 text-purple-900 border-2 border-purple-200 hover:border-purple-300 hover:shadow-2xl hover:scale-105',
      operator:
        'bg-gradient-to-br from-pink-300 to-purple-300 hover:from-pink-400 hover:to-purple-400 text-white border-2 border-pink-200 hover:shadow-2xl hover:scale-105',
      equals:
        'bg-gradient-to-br from-blue-400 to-cyan-400 hover:from-blue-500 hover:to-cyan-500 text-white border-2 border-blue-300 hover:shadow-2xl hover:scale-105',
      function:
        'bg-gradient-to-br from-purple-200 to-pink-200 hover:from-purple-300 hover:to-pink-300 text-purple-900 text-[10px] sm:text-xs md:text-sm border-2 border-purple-100 hover:shadow-2xl hover:scale-105',
      parenthesis:
        'bg-gradient-to-br from-purple-200 to-pink-200 hover:from-purple-300 hover:to-pink-300 text-purple-900 text-[10px] sm:text-xs md:text-sm border-2 border-purple-100 hover:shadow-2xl hover:scale-105',
    };

    return (
      <button onClick={onClick} className={`${baseClass} ${variants[variant]} ${className}`}>
        {children}
      </button>
    );
  };

  const currentYear = new Date().getFullYear();

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-100 via-purple-100 to-blue-100 flex flex-col">
      <h1
        className="text-xl sm:text-2xl md:text-3xl font-black text-center py-3 sm:py-4 md:py-6 bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 bg-clip-text text-transparent drop-shadow-lg animate-gradient flex-shrink-0"
        style={{
          backgroundSize: '200% 200%',
          animation: 'gradient 3s ease infinite',
        }}
      >
        🧮 Scientific Calculator 🧮
      </h1>

      <style>{`
        @keyframes gradient {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>

      <div className="flex-1 overflow-y-auto px-1 sm:px-2 md:px-4 pb-2 sm:pb-3 md:pb-4">
        <div className="w-full max-w-2xl mx-auto py-2 sm:py-3 md:py-4">
          <div className="bg-white/80 backdrop-blur-lg rounded-2xl sm:rounded-3xl shadow-2xl p-2 sm:p-4 md:p-6 border-2 sm:border-4 border-purple-200">
            <div className="mb-2 sm:mb-3">
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl sm:rounded-2xl p-2 sm:p-3 md:p-4 mb-2 sm:mb-3 border-2 border-purple-200 shadow-inner min-h-[60px] sm:min-h-[80px] md:min-h-[100px] flex items-end">
                <div className="text-purple-900 text-xl sm:text-2xl md:text-3xl font-bold text-right w-full overflow-x-auto break-all">
                  {display}
                </div>
              </div>

              <div className="flex gap-1.5 sm:gap-2 mb-2 sm:mb-3">
                <button
                  onClick={() => setIsRad(!isRad)}
                  className={`flex-1 py-1.5 sm:py-2 md:py-3 rounded-lg sm:rounded-xl font-bold text-xs sm:text-sm md:text-base transition-all duration-300 shadow-lg hover:shadow-2xl hover:scale-105 ${
                    isRad
                      ? 'bg-gradient-to-br from-purple-200 to-purple-300 hover:from-purple-300 hover:to-purple-400 text-purple-900 border-2 border-purple-200'
                      : 'bg-gradient-to-br from-blue-200 to-blue-300 hover:from-blue-300 hover:to-blue-400 text-blue-900 border-2 border-blue-200'
                  }`}
                >
                  {isRad ? 'RAD' : 'DEG'}
                </button>
                <button
                  onClick={handleBackspace}
                  className="flex-1 bg-gradient-to-br from-red-200 to-pink-200 hover:from-red-300 hover:to-pink-300 text-red-900 py-1.5 sm:py-2 md:py-3 rounded-lg sm:rounded-xl font-bold text-xs sm:text-sm md:text-base transition-all duration-300 shadow-lg hover:shadow-2xl hover:scale-105 flex items-center justify-center gap-1 sm:gap-2 border-2 border-red-200"
                >
                  <Delete size={14} className="sm:w-4 sm:h-4 md:w-5 md:h-5" /> DEL
                </button>
                <button
                  onClick={handleClear}
                  className="flex-1 bg-gradient-to-br from-green-200 to-teal-200 hover:from-green-300 hover:to-teal-300 text-green-900 py-1.5 sm:py-2 md:py-3 rounded-lg sm:rounded-xl font-bold text-xs sm:text-sm md:text-base transition-all duration-300 shadow-lg hover:shadow-2xl hover:scale-105 flex items-center justify-center gap-1 sm:gap-2 border-2 border-green-200"
                >
                  <RotateCcw size={14} className="sm:w-4 sm:h-4 md:w-5 md:h-5" /> AC
                </button>
              </div>
            </div>

            <div className="space-y-1.5 sm:space-y-2">
              <div className="grid grid-cols-5 gap-1.5 sm:gap-2">
                <Button variant="function" onClick={() => handleFunction('sin')}>sin</Button>
                <Button variant="function" onClick={() => handleFunction('cos')}>cos</Button>
                <Button variant="function" onClick={() => handleFunction('tan')}>tan</Button>
                <Button variant="function" onClick={() => handleFunction('ln')}>ln</Button>
                <Button variant="function" onClick={() => handleFunction('log')}>log</Button>
              </div>

              <div className="grid grid-cols-5 gap-1.5 sm:gap-2">
                <Button variant="function" onClick={() => handleFunction('x²')}>x²</Button>
                <Button variant="function" onClick={() => handleFunction('x³')}>x³</Button>
                <Button variant="operator" onClick={() => handleOperation('^')}>xʸ</Button>
                <Button variant="function" onClick={() => handleFunction('sqrt')}>√x</Button>
                <Button variant="function" onClick={() => handleFunction('1/x')}>1/x</Button>
              </div>

              <div className="grid grid-cols-5 gap-1.5 sm:gap-2">
                <Button variant="function" onClick={() => handleFunction('e^x')}>eˣ</Button>
                <Button variant="function" onClick={() => handleFunction('10^x')}>10ˣ</Button>
                <Button variant="function" onClick={() => handleConstant('π')}>π</Button>
                <Button variant="function" onClick={() => handleConstant('e')}>e</Button>
                <Button variant="function" onClick={() => handleOperation('!')}>n!</Button>
              </div>

              <div className="grid grid-cols-5 gap-1.5 sm:gap-2">
                <Button variant="parenthesis" onClick={() => handleParenthesis('(')}>(</Button>
                <Button variant="parenthesis" onClick={() => handleParenthesis(')')}>)</Button>
                <Button variant="operator" onClick={handlePercentage}>%</Button>
                <Button variant="operator" onClick={() => handleOperation('÷')}>÷</Button>
                <Button variant="operator" onClick={() => handleOperation('×')}>×</Button>
              </div>

              <div className="grid grid-cols-4 gap-1.5 sm:gap-2">
                <Button onClick={() => handleNumber('7')}>7</Button>
                <Button onClick={() => handleNumber('8')}>8</Button>
                <Button onClick={() => handleNumber('9')}>9</Button>
                <Button variant="operator" onClick={() => handleOperation('-')}>−</Button>

                <Button onClick={() => handleNumber('4')}>4</Button>
                <Button onClick={() => handleNumber('5')}>5</Button>
                <Button onClick={() => handleNumber('6')}>6</Button>
                <Button variant="operator" onClick={() => handleOperation('+')}>+</Button>

                <Button onClick={() => handleNumber('1')}>1</Button>
                <Button onClick={() => handleNumber('2')}>2</Button>
                <Button onClick={() => handleNumber('3')}>3</Button>
                <Button variant="operator" onClick={handleDecimal}>.</Button>

                <Button onClick={() => handleNumber('0')} className="col-span-2">0</Button>
                <Button variant="equals" onClick={handleEquals} className="col-span-2">
                  Calculate
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="text-center py-2 sm:py-3 md:py-4 text-purple-800 font-semibold text-xs sm:text-sm md:text-base flex-shrink-0 bg-purple-200">
        Made with 💖 by Yusra © {currentYear}
      </div>
    </div>
  );
}

export default ScientificCalculator;